import { useInfiniteQuery } from "@tanstack/react-query";
import {
  roleService,
  RoleFilters,
  RolesResponse,
} from "../api/services/roleService";
import type { Role } from "../schemas/roleSchema";
import { useRoles } from "./useRoles";
import { DEFAULT_PAGE_SIZE } from "../constants";

export type RolesInfiniteFilters = Omit<RoleFilters, "page" | "pageSize"> & {
  pageSize?: number;
};

export const useRolesInfinite = (filters: RolesInfiniteFilters = {}) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;
  const mutations = useRoles(undefined, { enabled: false });

  const query = useInfiniteQuery<RolesResponse, Error>({
    queryKey: ["roles", "infinite", { ...rest, pageSize }],
    queryFn: ({ pageParam }) =>
      roleService.getAll({
        ...rest,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined,
  });

  const roles: Role[] = query.data?.pages.flatMap((p) => p.data) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    ...mutations,
    roles,
    total,
    isLoading: query.isLoading || mutations.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
